/**
 * frame_clock — "run this on the next RENDERED frame", on whichever loop is rendering (XR P0.5, 2026-09-28).
 *
 * THE WINDOW-rAF-IN-XR LAW. While an immersive session presents, the session owns the frame loop and the page's
 * `window.requestAnimationFrame` stops firing (Quest Browser; caught live 2026-08-16 when city assembly parked
 * inside its warm loop). Anything that counts frames on the window loop stalls for the whole session: the deferred
 * GPU disposal (`gpu_dispose.ts`) never freed a single resource in VR — a leak on exactly the device whose browser
 * loses its context under memory pressure — and a VFX sprite animated by rAF froze on its first frame.
 *
 * `nextFrame` asks the XR session's loop while presenting and the window's otherwise. A session that ENDS drops its
 * pending callbacks, and one that STARTS silences the window's, so a request not answered within `REARM_MS` asks
 * again on whichever loop is rendering then. The re-ask never fires the callback itself — only a rendered frame does
 * (the disposal contract: a wall-clock backstop would free a batch inside a stalled frame's in-flight submit).
 */

interface RafSource { requestAnimationFrame(cb: (time: number, frame?: unknown) => void): number }

let xrSource: (() => RafSource | null) | null = null;

/** main.ts registers the renderer's XR session here (null while not presenting). */
export function setXrFrameSource(fn: (() => RafSource | null) | null): void { xrSource = fn; }

/** The session loop while presenting, else null. Never throws. */
function sessionLoop(): RafSource | null {
  try { return xrSource?.() ?? null; } catch { return null; }
}

/** True while frames come from an XR session (for callers that want to say so in a diagnostic). */
export function framesFromXr(): boolean { return sessionLoop() !== null; }

export const REARM_MS = 1000;

/** Call `cb` once, on the next frame the page renders. */
export function nextFrame(cb: () => void): void {
  let fired = false;
  const run = (): void => { if (fired) return; fired = true; cb(); };
  const ask = (): void => {
    if (fired) return;
    const s = sessionLoop();
    let asked = false;
    if (s) { try { s.requestAnimationFrame(run); asked = true; } catch { /* the session just ended */ } }
    if (!asked) requestAnimationFrame(run);
    setTimeout(ask, REARM_MS);
  };
  ask();
}

/** Call `cb` once `n` rendered frames have passed (n ≥ 1). */
export function afterFrames(n: number, cb: () => void): void {
  let left = Math.max(1, Math.floor(n));
  const step = (): void => { if (--left <= 0) cb(); else nextFrame(step); };
  nextFrame(step);
}
